import { ActionMenu } from 'common-ui/components/Table/ActionMenu'
import { useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router'
import Add from '@mui/icons-material/Add'
import Cached from '@mui/icons-material/Cached'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Delete from '@mui/icons-material/Delete'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import type { SortOrder } from 'common/api/v1/types'
import { ListUserSortableField, Role, type StaffExternalUserEntry } from 'common-billing-server/types'
import type { Query } from 'common/query'

import { useBillingApi } from '../../api/billing/billing-context'
import { formatDateTime } from '../../format-date'
import { makeExternalUserPath, Path } from '../../routes/path'
import { formatExternalRole } from '../customer/externalRoleDisplay'
import { useConfirmationDialog } from '../common/ConfirmationDialog'
import { RoleBasedComponent } from '../common/RoleBasedLink'
import { SelectionActions } from '../common/ListPageToolbar'
import { RotatingButton } from '../common/RotatingButton'
import { Column, DataFetchingPaginatedTable, RefreshableDataFetchingPaginatedTable } from '../common/Table'
import Wrapper from '../common/Wrapper'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import type { PaginatedDataFetchingFilter } from '../common/hooks/hook-paginated-data-fetching'

export const ListExternalUsersPage = () => {
    const billingApi = useBillingApi()
    const navigate = useNavigate()
    const tableRef = useRef<RefreshableDataFetchingPaginatedTable>(null)
    const [selectedEntries, setSelectedEntries] = useState<StaffExternalUserEntry[]>([])
    const showConfirmDeleteDialog = useConfirmationDialog()
    const { addFailedOperation, errorBanner } = useFailedOperationBanner()

    const fetchUsers = async ({
        filter,
        order,
        ...query
    }: Query<PaginatedDataFetchingFilter<Record<string, never>>, SortOrder<ListUserSortableField>>) => {
        try {
            return await billingApi.listExternalUserEntries({
                ...query,
                filter: { username: filter?.searchString },
                order,
            })
        } catch (error) {
            addFailedOperation({
                id: 'list-external-users',
                message: 'Failed fetching external users',
                error,
                retryFn: tableRef.current?.refreshData,
            })
            throw error
        }
    }

    const removeSelectedEntries = async (entries: StaffExternalUserEntry[]) => {
        for (const entry of entries) {
            const email = entry.kind === 'user' ? entry.user.username : entry.invitation.email
            try {
                const invitationId = entry.kind === 'user' ? entry.pendingActivation?.id : entry.invitation.id
                if (!invitationId && entry.kind === 'user') {
                    await billingApi.deleteExternalUser(entry.user.id)
                } else if (invitationId) {
                    await billingApi.deleteExternalUserInvitation(invitationId)
                }
            } catch (error) {
                addFailedOperation({
                    id: entry.id,
                    message: `Failed removing "${email}"`,
                    error,
                    retryFn: () => void removeSelectedEntries([entry]),
                })
            }
        }
        tableRef.current?.refreshData()
    }

    const revokeInvitation = async (id: string, email: string) => {
        try {
            await billingApi.deleteExternalUserInvitation(id)
            tableRef.current?.refreshData()
        } catch (error) {
            addFailedOperation({
                id,
                message: `Failed revoking the invitation for "${email}"`,
                error,
                retryFn: () => void revokeInvitation(id, email),
            })
        }
    }

    const columns: Column<StaffExternalUserEntry, ListUserSortableField>[] = [
        {
            title: 'Email',
            sortField: ListUserSortableField.userName,
            valueForColumn: (entry) =>
                entry.kind === 'user' ? (
                    <NavLink to={makeExternalUserPath(entry.user.id)}>{entry.user.username}</NavLink>
                ) : (
                    entry.invitation.email
                ),
        },
        {
            title: 'Customers',
            valueForColumn: (entry) =>
                entry.kind === 'user'
                    ? entry.user.customerAccess.map(({ customer }) => customer.name).join(', ')
                    : entry.invitation.organization.name,
        },
        {
            title: 'Status',
            valueForColumn: (entry) =>
                entry.kind === 'invitation' ? 'Invited' : entry.pendingActivation ? 'Pending activation' : 'Active',
        },
        {
            title: 'Role',
            valueForColumn: (entry) =>
                entry.kind === 'user'
                    ? entry.user.customerAccess
                          .map(({ customer, role }) => `${customer.name}: ${formatExternalRole(role)}`)
                          .join(', ')
                    : formatExternalRole(entry.invitation.role),
        },
        {
            title: 'Expires (UTC)',
            valueForColumn: (entry) =>
                entry.kind === 'invitation'
                    ? formatDateTime(entry.invitation.expiresAt)
                    : entry.pendingActivation
                      ? formatDateTime(entry.pendingActivation.expiresAt)
                      : '—',
        },
        {
            title: '',
            isClickable: false,
            valueForColumn: (entry) => {
                const email = entry.kind === 'user' ? entry.user.username : entry.invitation.email
                if (entry.kind === 'user' && !entry.pendingActivation) {
                    const view = () => navigate(makeExternalUserPath(entry.user.id))
                    return (
                        <ActionMenu options={[{ label: 'View user', icon: <ChevronRight />, onClick: view }]}>
                            <Tooltip title="View user">
                                <IconButton aria-label={`View details for ${email}`} onClick={view}>
                                    <ChevronRight />
                                </IconButton>
                            </Tooltip>
                        </ActionMenu>
                    )
                }
                const id = entry.kind === 'user' ? entry.pendingActivation!.id : entry.invitation.id
                const confirmation =
                    entry.kind === 'user'
                        ? `Revoke the invitation for ${email} and remove the unactivated account and all its customer access?`
                        : `Revoke the invitation for ${email} to ${entry.invitation.organization.name}?`
                const revoke = () =>
                    showConfirmDeleteDialog(() => void revokeInvitation(id, email), confirmation, {
                        ok: { text: 'Revoke', color: 'error' },
                    })
                return (
                    <ActionMenu
                        options={[
                            { label: 'Revoke invitation', icon: <Delete />, onClick: revoke, isDestructive: true },
                        ]}
                    >
                        <Tooltip title="Revoke invitation">
                            <IconButton aria-label={`Revoke invitation for ${email}`} onClick={revoke}>
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </ActionMenu>
                )
            },
        },
    ]

    return (
        <Wrapper
            fillHeight
            actions={
                <>
                    <RotatingButton
                        key="refresh"
                        tooltip="Refresh"
                        onClick={() => tableRef.current?.refreshData()}
                        Component={<Cached />}
                    />
                    <RoleBasedComponent key="create" minimumAllowedRole={Role.licenseAdmin}>
                        <NavLink to={Path.createExternalUser}>
                            <Tooltip title="Create new user">
                                <IconButton>
                                    <Add />
                                </IconButton>
                            </Tooltip>
                        </NavLink>
                    </RoleBasedComponent>
                </>
            }
        >
            <DataFetchingPaginatedTable
                stickyHeader
                myRef={tableRef}
                api={fetchUsers}
                columns={columns}
                emptyRowsMessage="No external users or pending invitations found"
                onRowClicked={(entry) => {
                    if (entry.kind === 'user') navigate(makeExternalUserPath(entry.user.id))
                }}
                isRowClickable={(entry) => entry.kind === 'user'}
                onRowsSelected={setSelectedEntries}
                selectionActions={
                    <SelectionActions
                        numberOfSelectedItems={selectedEntries.length}
                        selectedActions={[
                            <Tooltip key="delete" title="Remove selected users and invitations">
                                <IconButton
                                    onClick={() =>
                                        showConfirmDeleteDialog(
                                            () => void removeSelectedEntries(selectedEntries),
                                            `Remove ${selectedEntries.length} selected user(s) and invitation(s)? Unactivated accounts and their customer access will be removed.`
                                        )
                                    }
                                >
                                    <Delete />
                                </IconButton>
                            </Tooltip>,
                        ]}
                    />
                }
                searchParameters={{}}
            />
            {errorBanner}
        </Wrapper>
    )
}
